// What a task added with no edit panel starts as, from the person's own settings: quick add and
// "Create a task from this event" both start here.

import type { Scope, Settings } from '@/types'

export interface NewTaskDefaults {
  scope: Scope
  /** Who's doing it if the task is shared: the person adding it, or null for either of us. */
  assigneeUid: string | null
}

/** Private for a new account; for either of us unless the person has picked Me. */
export function newTaskDefaults(settings: Pick<Settings, 'defaultVisibility' | 'defaultAssignee'> | undefined, uid: string): NewTaskDefaults {
  return {
    scope: settings?.defaultVisibility ?? 'private',
    assigneeUid: settings?.defaultAssignee === 'me' ? uid : null,
  }
}

/** Quick add's who toggle: Mine, Shared (either of us) or the other person's. */
export type NewTaskWho = 'mine' | 'shared' | 'partner'

/** Where the toggle starts: the person's own "New tasks are for" setting. */
export function whoFromDefaults(defaults: NewTaskDefaults): NewTaskWho {
  return defaults.assigneeUid ? 'mine' : 'shared'
}

/**
 * The next choice a tap on the toggle steps to, in the order Mine, Shared, the other person's.
 * Before the other person has joined it only has Mine and Shared.
 */
export function nextWho(who: NewTaskWho, hasPartner: boolean): NewTaskWho {
  const order: NewTaskWho[] = hasPartner ? ['mine', 'shared', 'partner'] : ['mine', 'shared']
  return order[(order.indexOf(who) + 1) % order.length] ?? 'mine'
}

/** A private task is always the person's own, so the lock shows as Mine whatever was picked. */
export function shownWho(who: NewTaskWho, isPrivate: boolean): NewTaskWho {
  return isPrivate ? 'mine' : who
}

/** What a quick-add task is written with: a private one has no assignee; a shared one is for whoever was picked. */
export function newTaskPeople(who: NewTaskWho, isPrivate: boolean, uid: string, otherUid: string | null): { scope: Scope; assigneeUid: string | null } {
  if (isPrivate) return { scope: 'private', assigneeUid: null }
  if (who === 'mine') return { scope: 'shared', assigneeUid: uid }
  if (who === 'partner' && otherUid) return { scope: 'shared', assigneeUid: otherUid }
  return { scope: 'shared', assigneeUid: null }
}
